import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import FlipDigit from "./FlipDigit";
import MagneticButton from "./MagneticButton";

function useCountdown(targetIso) {
  const [diff, setDiff] = useState(() => Date.parse(targetIso) - Date.now());
  useEffect(() => {
    const t = setInterval(
      () => setDiff(Date.parse(targetIso) - Date.now()),
      1000
    );
    return () => clearInterval(t);
  }, [targetIso]);
  const past = diff <= 0;
  const r = past ? 0 : diff;
  return {
    past,
    days: Math.floor(r / 86400000),
    hours: Math.floor((r % 86400000) / 3600000),
    mins: Math.floor((r % 3600000) / 60000),
    secs: Math.floor((r % 60000) / 1000),
  };
}

const TIERS = [
  {
    id: "phase-1",
    title: "Phase 1",
    sub: "General Admission",
    price: "₹ 4,499",
    note: "3-day pass · Limited",
    sold: true,
  },
  {
    id: "phase-2",
    title: "Phase 2",
    sub: "General Admission",
    price: "₹ 5,499",
    note: "3-day pass · Available",
    featured: true,
  },
  {
    id: "desert-vip",
    title: "Desert VIP",
    sub: "Elevated deck",
    price: "₹ 12,999",
    note: "3-day pass · 400 cap",
  },
];

export default function WhenWhere() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const yImg = useTransform(scrollYProgress, [0, 1], ["8%", "-8%"]);

  const target = "2022-06-10T16:00:00+05:30";
  const { past, days, hours, mins, secs } = useCountdown(target);
  const [selected, setSelected] = useState("phase-2");

  return (
    <section
      ref={ref}
      id="passes"
      data-testid="venue-section"
      className="relative bg-[#0C0A09] py-24 lg:py-36 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left: Date + Venue + Countdown */}
          <div className="lg:col-span-5">
            <p className="text-[11px] font-semibold tracking-[0.32em] uppercase text-[#F59E0B] mb-4">
              <span className="tech-tag">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22D3EE] animate-pulse" />
                02 / WHEN & WHERE
              </span>
            </p>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tight uppercase leading-[0.95] text-[#FFF7ED]">
              <span className="font-serif-italic text-[#EA580C] font-normal">
                Three
              </span>{" "}
              nights.
              <br />
              One horizon.
            </h2>

            <div className="mt-10 space-y-6">
              <div data-testid="venue-date" className="border-t border-[#292524] pt-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                  When
                </div>
                <div className="mt-1 font-display text-xl lg:text-2xl font-bold text-[#FFF7ED]">
                  Friday 10 — Sunday 12, June 2022
                </div>
                <div className="text-sm text-stone-400">Gates 16:00 IST</div>
              </div>
              <div data-testid="venue-location" className="border-t border-[#292524] pt-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                  Where
                </div>
                <div className="mt-1 font-display text-xl lg:text-2xl font-bold text-[#FFF7ED]">
                  Embassy International Riding School
                </div>
                <div className="text-sm text-stone-400">
                  Kempapura · North Bengaluru
                </div>
                <div className="mt-2 readout text-[10px] tracking-[0.2em] text-[#22D3EE]">
                  13.1986° N / 77.7066° E · ELEV 908M
                </div>
                <a
                  href="https://maps.google.com/?q=Embassy+International+Riding+School+Bangalore"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="venue-map-link"
                  className="mt-3 inline-flex items-center gap-2 text-[#EA580C] text-xs font-semibold uppercase tracking-[0.24em] hover:gap-3 transition-all"
                >
                  Open in maps
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M13 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="square"
                    />
                  </svg>
                </a>
              </div>
            </div>

            {/* Flip countdown */}
            <div className="mt-10">
              <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500 mb-4">
                {past ? "Edition concluded" : "Countdown"}
              </div>
              <div
                data-testid="venue-countdown"
                className="flex items-start gap-3 lg:gap-4"
              >
                <FlipDigit value={days} label="DAYS" />
                <FlipDigit value={hours} label="HRS" />
                <FlipDigit value={mins} label="MIN" />
                <FlipDigit value={secs} label="SEC" />
              </div>
              <p className="mt-4 text-xs text-stone-500 max-w-xs">
                {past
                  ? "This edition has concluded — subscribe to hear about the next chapter first."
                  : "Counting down to gates open."}
              </p>
            </div>
          </div>

          {/* Right: venue image + ticket tiers */}
          <div className="lg:col-span-7">
            <motion.div
              style={{ y: yImg }}
              className="relative aspect-[16/10] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1674789317870-2c412ef13b34?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800"
                alt="Open-air festival at night"
                data-testid="venue-image"
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0C0A09] via-[#0C0A09]/10 to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#F59E0B]">
                    Open Air
                  </div>
                  <div className="font-display text-lg lg:text-xl font-bold text-[#FFF7ED]">
                    Funktion-One rig · 18+ with ID
                  </div>
                </div>
                <div className="hidden md:flex items-center gap-2 tech-tag">
                  <span className="eq" aria-hidden style={{ height: "16px" }}>
                    <span /><span /><span /><span /><span />
                  </span>
                  LIVE · 96 KHZ
                </div>
              </div>
            </motion.div>

            {/* Passes */}
            <div className="mt-8">
              <div className="flex items-baseline justify-between mb-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                  Passes
                </div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                  Incl. GST · Non-transferable
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#292524]">
                {TIERS.map((t) => {
                  const isActive = selected === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => !t.sold && setSelected(t.id)}
                      data-testid={`ticket-tier-${t.id}`}
                      className={`tier-card text-left p-6 flex flex-col relative ${
                        t.sold ? "cursor-not-allowed" : "cursor-pointer"
                      } ${
                        isActive
                          ? "bg-[#1C1917] ring-1 ring-[#EA580C] ring-inset"
                          : "bg-[#0C0A09]"
                      }`}
                    >
                      {t.featured && (
                        <span className="absolute -top-2.5 left-6 bg-[#EA580C] text-white text-[9px] font-bold tracking-[0.3em] uppercase px-2 py-0.5">
                          Popular
                        </span>
                      )}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#F59E0B]">
                            {t.title}
                          </div>
                          <div className="mt-1 font-display text-base font-bold text-[#FFF7ED]">
                            {t.sub}
                          </div>
                          <div className="mt-1 readout text-[9px] tracking-[0.24em] text-[#22D3EE]/80">
                            SKU · SB-BLR22-{t.id.toUpperCase()}
                          </div>
                        </div>
                        {t.sold && (
                          <span className="text-[9px] font-bold tracking-[0.24em] uppercase text-stone-500 border border-stone-700 px-1.5 py-0.5">
                            Sold Out
                          </span>
                        )}
                      </div>
                      <div className="mt-4">
                        <div
                          className={`font-display text-2xl lg:text-3xl font-black ${
                            t.sold
                              ? "text-stone-600 line-through"
                              : "text-[#FFF7ED]"
                          }`}
                        >
                          {t.price}
                        </div>
                        <div className="mt-0.5 text-[11px] text-stone-400">
                          {t.note}
                        </div>
                      </div>
                      <div className="mt-4 text-[10px] font-bold uppercase tracking-[0.28em]">
                        <span className={isActive ? "text-[#EA580C]" : "text-stone-500"}>
                          {t.sold
                            ? "Unavailable"
                            : isActive
                            ? "Selected"
                            : "Select"}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-[#292524] pt-6">
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-stone-500">
                    Selected
                  </div>
                  <div
                    className="font-display text-base font-bold text-[#FFF7ED]"
                    data-testid="tickets-selected-label"
                  >
                    {TIERS.find((t) => t.id === selected)?.title} ·{" "}
                    {TIERS.find((t) => t.id === selected)?.sub}
                  </div>
                </div>
                <MagneticButton
                  as="a"
                  href="https://in.bookmyshow.com/explore/events-bengaluru"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="tickets-checkout-cta"
                  className="inline-flex items-center gap-3 bg-[#EA580C] text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.28em] hover:bg-[#F59E0B] hover:text-[#0C0A09] transition-colors"
                >
                  Continue on partner
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M5 12h14M13 5l7 7-7 7"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="square"
                    />
                  </svg>
                </MagneticButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
